import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { AccountStatus, Option, PricingModel } from './types';
import { axios } from '../api/axios';
import { Spinner } from '../assets/Spinner';
import { Dropdown } from '../component/Dropdown';
import { TextInput } from '../component/TextInput';
import { Button } from '../component/buttons/Button';
import { countryOptions } from '../dropdownOptions';
import { BOTTOM_TOAST } from '../utils/toastUtils';

type LeadSearchResult = {
  id: string;
  email: string;
  account_id: string | null;
  account_status: string | null;
  account_gladly_customer_id: string | null;
  country: string;
  pricing_model: PricingModel | null;
};

const stripEmpty = (obj: Record<string, string>) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== ''));

export const LeadLookup = () => {
  const navigate = useNavigate();
  const [urlSearchParams, setUrlSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [country, setCountry] = useState<Option | undefined>(undefined);

  useEffect(() => {
    const emailParam = urlSearchParams.get('email') ?? '';
    if (!emailParam) return;

    setEmail(emailParam);
    fetchLead({ email: emailParam, country: urlSearchParams.get('country') ?? '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlSearchParams]);

  const clearParams = (): void => setUrlSearchParams({}, { replace: true });

  const fetchLead = async (params: { email: string; country: string }) => {
    setIsLoading(true);
    try {
      const payload = stripEmpty(params);
      const { data: searchResults } = await axios.post<LeadSearchResult[]>('/lead_search', payload);
      if (searchResults.length > 1) {
        toast.error(
          'More than one lead profile exists for this email address. Please reach out to your Team Lead.',
          BOTTOM_TOAST
        );
        clearParams();
        return;
      }
      if (searchResults.length === 0) {
        toast.error('No results found.', BOTTOM_TOAST);
        clearParams();
        return;
      }
      const leadResult = searchResults[0];
      if (
        leadResult.account_status === AccountStatus.Active ||
        leadResult.account_status === AccountStatus.Cancelled
      ) {
        toast.error(
          'This email address is associated with an active or cancelled account. Please use Customer Lookup to view their account.',
          BOTTOM_TOAST
        );
        clearParams();
        return;
      }
      navigate(`/lead-lookup/${leadResult.id}?email=${encodeURIComponent(leadResult.email)}`, {
        state: { lead: leadResult },
        replace: true,
      });
    } catch (e: unknown) {
      const response = (e as { response?: { status?: number; data?: { email?: string[] } } })
        ?.response;
      const status = response?.status;
      const apiMessage = response?.data?.email?.[0];
      const message =
        status === 404
          ? 'No results found.'
          : apiMessage ?? 'Something went wrong. Please try again.';
      toast.error(message, BOTTOM_TOAST);
      clearParams();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = () => {
    if (!email) return;
    const sanitized = stripEmpty({ email, country: country?.value ?? '' });
    setUrlSearchParams(sanitized);
  };

  if (isLoading) return <Spinner />;

  return (
    <div className="flex h-[calc(100vh-77px)] overflow-auto items-start justify-center bg-slate50 px-4">
      <div className="min-w-[300px] w-full flex items-start justify-center">
        <div className="w-full h-full max-w-screen-lg flex flex-col items-center justify-start pt-20 gap-4">
          <h1>Lead Lookup</h1>
          <div className="w-full flex h-full items-start justify-center">
            <div className="flex flex-col md:flex-row justify-center items-end gap-9">
              <div className="w-full md:w-[320px] flex flex-col gap-1">
                <div>Lead's Email</div>
                <TextInput
                  className="h-[38px]"
                  value={email}
                  setTextChange={setEmail}
                  onEnter={handleSearch}
                />
              </div>
              <div className="w-full md:w-[320px]">
                <Dropdown
                  title="Country (optional)"
                  options={countryOptions}
                  selectedOption={country}
                  setSelectedOption={setCountry}
                  noDefaultSelection
                />
              </div>
            </div>
          </div>
          <div className="mt-5">
            <Button label="Search" onClick={handleSearch} isDisabled={!email} />
          </div>
        </div>
      </div>
    </div>
  );
};
